import { useEffect, useMemo, useState } from 'react'
import { useAuth, useSocket } from '@/context'
import type { AnalysisPresenceUpdateEvent, ResourcePresenceViewer } from '@/types/websocket'
import { excludeSelf } from '@/utils/presence'
import { getPresenceDisplayName } from '@/utils/userProfile'

export function useAnalysisPresence(
	analysisId: string | null | undefined
): ResourcePresenceViewer[] {
	const { socket, connected } = useSocket()
	const { user } = useAuth()
	const [viewers, setViewers] = useState<ResourcePresenceViewer[]>([])

	useEffect(() => {
		if (!socket || !connected || !analysisId) {
			setViewers([])
			return
		}

		const handlePresenceUpdate = (data: AnalysisPresenceUpdateEvent) => {
			if (String(data.analysis_id) !== String(analysisId)) return
			setViewers(data.viewers ?? [])
		}

		socket.on('analysis:presence:update', handlePresenceUpdate)

		if (user?.id) {
			socket.emit('subscribe_analysis', {
				analysis_id: analysisId,
				display_name: getPresenceDisplayName(user),
			})
		}

		return () => {
			socket.off('analysis:presence:update', handlePresenceUpdate)
		}
	}, [socket, connected, analysisId, user])

	return useMemo(() => excludeSelf(viewers, user?.id), [viewers, user?.id])
}
